"use client";

import SkeletonCard from "@/components/SkeletonCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api from "@/lib/axios";
import { cn } from "@/lib/utils";
import { AxiosError } from "axios";
import {
  Calendar,
  ExternalLink,
  FileText,
  GraduationCap,
  Loader2,
  Mail,
  MapPin,
  Phone,
  School,
  Trophy,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface DataPendaftaranCustom {
  id_data_pendaftaran: number;
  nomor_pendaftaran: string;
  nisn: string;
  nama_lengkap: string;
  handphone: string;
  email: string;
  jenis_kelamin: string;
  path_foto: string;
  npsn_sekolah: string;
  nama_sekolah: string;
  jalan: string;
  nama_kecamatan: string;
  nama_kabupaten: string;
  nama_provinsi: string;
  jalan_domisili: string;
  nama_kecamatan_domisili: string;
  nama_kabupaten_domisili: string;
  nama_provinsi_domisili: string;
  bakat_talent: string;
  tanggal_wawancara: string;
  sesi: number;
  nama_ruang: string;
  pilihan1: string;
  pilihan2: string;
  mapping_bidang_talent: string;
  id_data_distribusi_ruangan: number;
}

interface DataPrestasiCustom {
  id_data_pendaftaran: number;
  nama_jenis_prestasi: string;
  nama_jenjang_prestasi: string;
  nama_grup_prestasi: string;
  nama_capaian_prestasi: string;
  lembaga_pemberi_prestasi: string;
  tahun_prestasi: string;
  path_bukti_prestasi: string;
}

interface KomponenNilai {
  id_ref_komponen_penilaian_talent: number;
  kode: string;
  nama_ref_parent_komponen: string;
  id_ref_parent_komponen_penilaian_talent: number;
  nama_komponen: string;
  nilai_ver1: number;
  nilai_ver2: number;
}

interface ParentKomponenNilai {
  kode_parent: string;
  nama_ref_parent_komponen: "POTENSI";
  komponen: KomponenNilai[];
}

interface DetailPesertaModalProps {
  show: boolean;
  onClose: () => void;
  detailPeserta?: {
    pewawancaraKe: number;
    dataNilai: ParentKomponenNilai[];
    dataPendaftaran: DataPendaftaranCustom;
    dataPrestasi: DataPrestasiCustom[];
  };
  isPending: boolean;
}

const postDataSaveNilai = async ({
  pewawancaraKe,
  id_data_pendaftaran,
  id_ref_komponen_penilaian_talent,
  nilai,
}: {
  pewawancaraKe: number;
  id_data_pendaftaran: number;
  id_ref_komponen_penilaian_talent: number;
  nilai: number;
}) => {
  const response = await api.post(
    "/verifikator/peserta-wawancara-talent/save-nilai",
    {
      pewawancaraKe,
      id_data_pendaftaran,
      id_ref_komponen_penilaian_talent,
      nilai,
    },
  );

  return response.data;
};

const patchDataSaveJenisTalent = async ({
  id_data_distribusi_ruangan,
  mapping_bidang_talent,
}: {
  id_data_distribusi_ruangan: number;
  mapping_bidang_talent: string;
}) => {
  const response = await api.patch(
    "/verifikator/peserta-wawancara-talent/update-bidang-talent",
    {
      id_data_distribusi_ruangan,
      mapping_bidang_talent,
    },
  );

  return response.data;
};

export function DetailPeserta({
  show,
  onClose,
  detailPeserta,
  isPending,
}: DetailPesertaModalProps) {
  const [form, setForm] = useState({
    jenis_talent: detailPeserta?.dataPendaftaran?.mapping_bidang_talent || "",
  });

  const queryClient = useQueryClient();

  const { mutate: mutateSaveNilai } = useMutation({
    mutationFn: postDataSaveNilai,
    onSuccess: (data) => {
      setForm((prev) => ({ ...prev, jenis_talent: "" }));
      toast.success(data?.message || "Success.");
      queryClient.invalidateQueries({
        queryKey: ["detailPesertaTalentScouting"],
      });
      queryClient.invalidateQueries({
        queryKey: ["listDataPendaftaran"],
      });
      queryClient.invalidateQueries({
        queryKey: ["listDataPendaftaranAnalitik"],
      });
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const { mutate: mutateSaveJenisTalent, isPending: isPendingSaveJenisTalent } =
    useMutation({
      mutationFn: patchDataSaveJenisTalent,
      onSuccess: (data) => {
        setForm((prev) => ({ ...prev, jenis_talent: "" }));
        toast.success(data?.message || "Success.");
        queryClient.invalidateQueries({
          queryKey: ["detailPesertaTalentScouting"],
        });
        queryClient.invalidateQueries({
          queryKey: ["listDataPendaftaran"],
        });
        queryClient.invalidateQueries({
          queryKey: ["listDataPendaftaranAnalitik"],
        });
      },
      onError: (error: AxiosError) => {
        const err = error as AxiosError<{ message?: string }>;
        toast.error(
          err?.response?.data?.message || "An unexpected error occurred.",
        );
      },
    });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getJenisKelaminLabel = (jk: string) => {
    return jk === "L" ? "Laki-laki" : "Perempuan";
  };

  const ratingLabels = {
    5: "SANGAT BAIK",
    4: "BAIK",
    3: "CUKUP",
    2: "KURANG",
    1: "KURANG SEKALI",
  };

  const [_, setRatings] = useState<Record<string, number>>({});
  // const [savedItems] = useState<Set<string>>(new Set())

  const handleSaveNilai = (
    pewawancaraKe: number,
    id_data_pendaftaran: number,
    id_ref_komponen_penilaian_talent: number,
    nilai: number,
    componentIndex: number,
    childIndex: number,
  ) => {
    const key = `${componentIndex}-${childIndex}`;
    setRatings((prev) => ({
      ...prev,
      [key]: nilai,
    }));

    mutateSaveNilai({
      pewawancaraKe,
      id_data_pendaftaran,
      id_ref_komponen_penilaian_talent,
      nilai,
    });
  };

  const handleSaveJenisTalent = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutateSaveJenisTalent({
      id_data_distribusi_ruangan:
        detailPeserta?.dataPendaftaran?.id_data_distribusi_ruangan || 0,
      mapping_bidang_talent: form.jenis_talent,
    });
  };

  useEffect(() => {
    if (!detailPeserta?.dataPendaftaran?.mapping_bidang_talent) return;
    setForm((prev) => ({
      ...prev,
      jenis_talent: detailPeserta?.dataPendaftaran?.mapping_bidang_talent,
    }));
  }, [detailPeserta?.dataPendaftaran?.mapping_bidang_talent]);

  // const handleRatingClick = (
  //   componentIndex: number,
  //   childIndex: number,
  //   rating: number
  // ) => {
  //   const key = `${componentIndex}-${childIndex}`
  //   setRatings((prev) => ({
  //     ...prev,
  //     [key]: rating,
  //   }))
  // }

  // const handleSaveRating = (componentIndex: number, childIndex: number) => {
  //   const key = `${componentIndex}-${childIndex}`
  //   if (ratings[key]) {
  //     setSavedItems((prev) => new Set([...prev, key]))
  //
  //   }
  // }

  // const getRating = (componentIndex: number, childIndex: number) => {
  //   return ratings[`${componentIndex}-${childIndex}`] || 0
  // }

  // const isSaved = (componentIndex: number, childIndex: number) => {
  //   return savedItems.has(`${componentIndex}-${childIndex}`)
  // }

  return (
    <div>
      <Dialog open={show} onOpenChange={onClose}>
        {isPending ? (
          <DialogContent>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </DialogContent>
        ) : !detailPeserta ? null : (
          <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Detail Peserta - {detailPeserta?.dataPendaftaran?.nama_lengkap}
              </DialogTitle>
            </DialogHeader>

            <section className="grid grid-cols-2 gap-5">
              <div className="space-y-5">
                {/* Personal Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <User className="h-4 w-4" />
                      Informasi Pribadi
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Nama Lengkap
                        </label>
                        <p className="font-medium">
                          {detailPeserta?.dataPendaftaran?.nama_lengkap}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          NISN
                        </label>
                        <p className="font-medium">
                          {detailPeserta?.dataPendaftaran?.nisn}
                        </p>
                      </div>
                      <div className="row-span-2">
                        <div className="rounded-xl overflow-hidden">
                          <img
                            src={detailPeserta?.dataPendaftaran?.path_foto}
                            alt="foto-siswa"
                            className="h-40"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Nomor Pendaftaran
                        </label>
                        <p className="font-medium">
                          {detailPeserta?.dataPendaftaran?.nomor_pendaftaran}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Jenis Kelamin
                        </label>
                        <p className="font-medium">
                          {getJenisKelaminLabel(
                            detailPeserta?.dataPendaftaran?.jenis_kelamin,
                          )}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Contact Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Phone className="h-4 w-4" />
                      Informasi Kontak
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-500" />
                        <div>
                          <label className="text-sm font-medium text-gray-500">
                            Handphone
                          </label>
                          <p className="font-medium">
                            {detailPeserta?.dataPendaftaran?.handphone}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-500" />
                        <div>
                          <label className="text-sm font-medium text-gray-500">
                            Email
                          </label>
                          <p className="font-medium">
                            {detailPeserta?.dataPendaftaran?.email}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Address Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <MapPin className="h-4 w-4" />
                      Informasi Alamat
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium mb-2">Alamat KTP</h4>
                        <div className="space-y-1 text-sm">
                          <p>{detailPeserta?.dataPendaftaran?.jalan}</p>
                          <p>
                            {detailPeserta?.dataPendaftaran?.nama_kecamatan}
                          </p>
                          <p>
                            {detailPeserta?.dataPendaftaran?.nama_kabupaten}
                          </p>
                          <p>{detailPeserta?.dataPendaftaran?.nama_provinsi}</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">Alamat Domisili</h4>
                        <div className="space-y-1 text-sm">
                          <p>
                            {detailPeserta?.dataPendaftaran?.jalan_domisili}
                          </p>
                          <p>
                            {
                              detailPeserta?.dataPendaftaran
                                ?.nama_kecamatan_domisili
                            }
                          </p>
                          <p>
                            {
                              detailPeserta?.dataPendaftaran
                                ?.nama_kabupaten_domisili
                            }
                          </p>
                          <p>
                            {
                              detailPeserta?.dataPendaftaran
                                ?.nama_provinsi_domisili
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* School Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <School className="h-4 w-4" />
                      Informasi Sekolah
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Nama Sekolah
                        </label>
                        <p className="font-medium">
                          {detailPeserta?.dataPendaftaran?.nama_sekolah}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          NPSN
                        </label>
                        <p className="font-medium">
                          {detailPeserta?.dataPendaftaran?.npsn_sekolah}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Interview Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Calendar className="h-4 w-4" />
                      Informasi Wawancara
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Tanggal Wawancara
                        </label>
                        <p className="font-medium">
                          {formatDate(
                            detailPeserta?.dataPendaftaran?.tanggal_wawancara,
                          )}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Sesi
                        </label>
                        <Badge variant="outline">
                          Sesi {detailPeserta?.dataPendaftaran?.sesi}
                        </Badge>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Ruang
                        </label>
                        <p className="font-medium">
                          {detailPeserta?.dataPendaftaran?.nama_ruang}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Study Program Choices */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <GraduationCap className="h-4 w-4" />
                      Pilihan Program Studi
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Pilihan 1
                        </label>
                        <p className="font-medium text-blue-600">
                          {detailPeserta?.dataPendaftaran?.pilihan1}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Pilihan 2
                        </label>
                        <p className="font-medium text-blue-600">
                          {detailPeserta?.dataPendaftaran?.pilihan2}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Talents & Interests */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="h-4 w-4" />
                      Bakat & Minat
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm leading-relaxed">
                      {detailPeserta?.dataPendaftaran?.bakat_talent}
                    </p>
                  </CardContent>
                </Card>

                {/* Achievements */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Trophy className="h-4 w-4" />
                      Data Prestasi
                    </CardTitle>
                    <CardDescription>
                      {detailPeserta?.dataPrestasi &&
                      detailPeserta?.dataPrestasi.length > 0
                        ? `Menampilkan ${detailPeserta?.dataPrestasi.length} prestasi`
                        : "Tidak ada data prestasi"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {detailPeserta?.dataPrestasi &&
                    detailPeserta?.dataPrestasi.length > 0 ? (
                      <div className="space-y-4">
                        {detailPeserta?.dataPrestasi.map((prestasi, index) => (
                          <Card
                            key={index}
                            className="border-l-4 border-l-yellow-500"
                          >
                            <CardContent className="pt-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    Jenis Prestasi
                                  </label>
                                  <p className="font-medium">
                                    {prestasi.nama_jenis_prestasi}
                                  </p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    Jenjang
                                  </label>
                                  <Badge variant="secondary">
                                    {prestasi.nama_jenjang_prestasi}
                                  </Badge>
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    Capaian
                                  </label>
                                  <Badge
                                    variant="default"
                                    className="bg-yellow-100 text-yellow-800"
                                  >
                                    {prestasi.nama_capaian_prestasi}
                                  </Badge>
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    Grup
                                  </label>
                                  <p className="font-medium">
                                    {prestasi.nama_grup_prestasi}
                                  </p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    Lembaga Pemberi
                                  </label>
                                  <p className="font-medium">
                                    {prestasi.lembaga_pemberi_prestasi}
                                  </p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    Tahun
                                  </label>
                                  <p className="font-medium">
                                    {prestasi.tahun_prestasi}
                                  </p>
                                </div>
                              </div>
                              {prestasi.path_bukti_prestasi && (
                                <div className="mt-4">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      window.open(
                                        prestasi.path_bukti_prestasi,
                                        "_blank",
                                      )
                                    }
                                    className="flex items-center gap-2"
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                    Lihat Bukti Prestasi
                                  </Button>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Tidak ada data prestasi</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader>
                  <h1 className="text-2xl font-semibold">
                    Form Penilaian Talent Scouting
                  </h1>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(ratingLabels)
                      .reverse()
                      .map(([score, label]) => (
                        <Badge
                          key={score}
                          variant="secondary"
                          className="text-xs"
                        >
                          {score} - {label}
                        </Badge>
                      ))}
                  </div>
                  <br />
                  <form
                    onSubmit={handleSaveJenisTalent}
                    className="flex items-center gap-3"
                  >
                    <h1 className="text-lg font-semibold whitespace-nowrap">
                      Jenis Talent
                    </h1>
                    <Input
                      placeholder="Ketik jenis talent..."
                      value={form.jenis_talent}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          jenis_talent: event.target.value,
                        }))
                      }
                    />
                    <Button disabled={isPendingSaveJenisTalent}>
                      {isPendingSaveJenisTalent ? (
                        <div className="flex">
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Processing...
                        </div>
                      ) : (
                        "Simpan"
                      )}
                    </Button>
                  </form>
                </CardHeader>

                <CardContent className="space-y-8">
                  {detailPeserta?.dataNilai?.map((item, componentIndex) => (
                    <div key={componentIndex} className="space-y-4">
                      <div className="flex items-center gap-3 pb-2 border-b">
                        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted text-sm font-medium">
                          {item.kode_parent}
                        </div>
                        <h2 className="text-lg font-semibold uppercase">
                          {item.nama_ref_parent_komponen}
                        </h2>
                      </div>

                      <div className="space-y-4 ml-4">
                        {item?.komponen?.map((subItem, childIndex) => {
                          // const currentRating = getRating(
                          //   componentIndex,
                          //   childIndex
                          // )
                          const currentRating =
                            detailPeserta?.pewawancaraKe == 1
                              ? subItem?.nilai_ver1
                              : subItem?.nilai_ver2;

                          // const itemSaved = isSaved(componentIndex, childIndex)

                          return (
                            <div
                              key={childIndex}
                              className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-card"
                            >
                              <div className="flex-1">
                                <p className="font-medium">
                                  {childIndex + 1}. {subItem?.nama_komponen}
                                </p>
                                {currentRating > 0 && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Nilai: {currentRating} (
                                    {
                                      ratingLabels[
                                        currentRating as keyof typeof ratingLabels
                                      ]
                                    }
                                    )
                                  </p>
                                )}
                              </div>

                              <div className="flex items-center gap-3">
                                <div className="flex gap-1">
                                  {Array(5)
                                    .fill(null)
                                    .map((_, ratingIndex) => {
                                      const ratingValue = ratingIndex + 1;
                                      const isSelected =
                                        subItem[
                                          detailPeserta?.pewawancaraKe == 1
                                            ? "nilai_ver1"
                                            : detailPeserta?.pewawancaraKe == 2
                                              ? "nilai_ver2"
                                              : ("" as keyof KomponenNilai)
                                        ] === ratingValue;

                                      return (
                                        <button
                                          key={ratingIndex}
                                          onClick={() => {
                                            handleSaveNilai(
                                              detailPeserta?.pewawancaraKe,
                                              detailPeserta?.dataPendaftaran
                                                ?.id_data_pendaftaran,
                                              subItem?.id_ref_komponen_penilaian_talent,
                                              ratingValue,
                                              componentIndex,
                                              childIndex,
                                            );
                                            // handleRatingClick(
                                            //   componentIndex,
                                            //   childIndex,
                                            //   ratingValue
                                            // )
                                          }}
                                          className={cn(
                                            "w-10 h-10 rounded-md border text-sm font-medium transition-colors",
                                            isSelected
                                              ? "bg-primary text-primary-foreground border-primary"
                                              : "bg-background hover:bg-muted border-input",
                                          )}
                                        >
                                          {ratingValue}
                                        </button>
                                      );
                                    })}
                                </div>

                                {/* {currentRating > 0 && (
                                  <Button
                                    onClick={() =>
                                      handleSaveRating(
                                        componentIndex,
                                        childIndex
                                      )
                                    }
                                    disabled={itemSaved}
                                    size="sm"
                                    variant={itemSaved ? "default" : "outline"}
                                  >
                                    {itemSaved ? (
                                      <>
                                        <Check className="w-4 h-4 mr-1" />
                                        Tersimpan
                                      </>
                                    ) : (
                                      <>
                                        <Save className="w-4 h-4 mr-1" />
                                        Simpan
                                      </>
                                    )}
                                  </Button>
                                )} */}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* <div className="flex justify-end pt-6 border-t">
                    <Button>
                      <Save className="w-4 h-4 mr-2" />
                      Simpan Semua Penilaian
                    </Button>
                  </div> */}
                </CardContent>
              </Card>
            </section>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
